import {
  DocumentTextIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';

import { ProjectItem, ProjectType } from '../../types/project';

const PROJECT_LINE_OF_BUSINESS_LABEL = 'Project Line of Business';
const NOT_SELECTED_PROJECT_LINE_OF_BUSINESS_LABEL = 'Not Selected';

export interface ProjectFormHandle {
  submitForm: () => void;
}

interface ProjectFormProps {
  mode: 'create' | 'edit';
  project?: ProjectItem;
  initialData?: Partial<FormData>;
  lineOfBusinessOptions?: readonly string[];
  onSubmit: (projectData: Partial<ProjectItem>) => void;
  onCancel: () => void;
  onFormChange?: (formData: FormData) => void;
  isLoading?: boolean;
  /** When true the internal Cancel / Submit action buttons are not rendered */
  hideActions?: boolean;
}

interface FormData {
  title: string;
  projectLineOfBusiness: string;
  description: string;
  projectType: ProjectType | '';
}

interface FormErrors {
  title?: string;
  projectLineOfBusiness?: string;
  description?: string;
  projectType?: string;
}

/**
 * ProjectForm - Unified component for both creating new projects and editing existing ones
 * Supports both Green Field and Brown Field project types with conditional fields
 */
export const ProjectForm = forwardRef<ProjectFormHandle, ProjectFormProps>(({
  mode,
  project,
  initialData,
  lineOfBusinessOptions = [],
  onSubmit,
  onCancel,
  onFormChange,
  isLoading = false,
  hideActions = false,
}, ref) => {
  const [formData, setFormData] = useState<FormData>({
    title: initialData?.title ?? project?.title ?? '',
    projectLineOfBusiness:
      initialData?.projectLineOfBusiness ??
      project?.projectLineOfBusiness ??
      project?.line_of_business ??
      '',
    description: initialData?.description ?? project?.description ?? '',
    projectType: initialData?.projectType ?? project?.projectType ?? '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Track original project type for edit mode warnings
  const [originalProjectType] = useState(project?.projectType);
  const isProjectTypeChangeLocked = Boolean(
    mode === 'edit' &&
      originalProjectType &&
      formData.projectType !== originalProjectType,
  );

  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => formRef.current?.requestSubmit(),
  }));

  const titleInputId = 'project-title';
  const projectLineOfBusinessInputId = 'project-line-of-business';
  const descriptionInputId = 'project-description';
  const textFieldClasses =
    'block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none ' +
    'focus:ring-2 focus:ring-[#0097a9] focus:border-[#0097a9]';

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: string) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    
    // Call the form change callback if provided
    if (onFormChange) {
      onFormChange(updatedData);
    }
    
    // Clear validation error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Project title must be at least 3 characters';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Project title must be less than 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Project description must be at least 10 characters';
    }

    if (!formData.projectType) {
      newErrors.projectType = 'Project type is required';
    }

    if (isProjectTypeChangeLocked) {
      newErrors.projectType = 'Project type cannot be changed after creation';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {return;}

    const projectData: Partial<ProjectItem> = {
      title: formData.title.trim(),
      projectLineOfBusiness:
        formData.projectLineOfBusiness.trim().length > 0
          ? formData.projectLineOfBusiness
          : null,
      description: formData.description.trim(),
      projectType: formData.projectType as ProjectType,
    };

    onSubmit(projectData);
  };

  return (
    <div className="w-full">
      <div className="bg-white shadow-lg rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5" />
            {mode === 'create' ? 'Create New Project' : 'Edit Project'}
          </h2>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Project Title */}
            <div>
              <label
                htmlFor={titleInputId}
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Project Title *
              </label>
              <input
                id={titleInputId}
                type="text"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                className={`${textFieldClasses} ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter project title..."
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Project Description */}
            <div>
              <label
                htmlFor={projectLineOfBusinessInputId}
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {PROJECT_LINE_OF_BUSINESS_LABEL}
              </label>
              <select
                id={projectLineOfBusinessInputId}
                value={formData.projectLineOfBusiness}
                onChange={(e) =>
                  handleFieldChange('projectLineOfBusiness', e.target.value)
                }
                className={`${textFieldClasses} border-gray-300 bg-white`}
              >
                <option value="">{NOT_SELECTED_PROJECT_LINE_OF_BUSINESS_LABEL}</option>
                {lineOfBusinessOptions.map((lineOfBusinessOption) => (
                  <option key={lineOfBusinessOption} value={lineOfBusinessOption}>
                    {lineOfBusinessOption}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Description */}
            <div>
              <label
                htmlFor={descriptionInputId}
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Project Description *
              </label>
              <textarea
                id={descriptionInputId}
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={4}
                className={`${textFieldClasses} ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe the project purpose, scope, and objectives..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Project Type */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-1">
                Project Type *
              </legend>
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
                The project type can&apos;t be changed once you progress past
                setup, so choose carefully.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Green Field Option */}
                <button
                  type="button"
                  className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all text-left ${
                    formData.projectType === ProjectType.GREEN_FIELD
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleFieldChange('projectType', ProjectType.GREEN_FIELD)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      formData.projectType === ProjectType.GREEN_FIELD ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <SparklesIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Green Field</h3>
                      <p className="text-sm text-gray-500">New projects starting from scratch</p>
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="projectType"
                    value="green_field"
                    checked={formData.projectType === ProjectType.GREEN_FIELD}
                    onChange={() => { /* Handled by button click */ }}
                    className="absolute top-4 right-4"
                  />
                </button>

                {/* Brown Field Option */}
                <button
                  type="button"
                  className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all text-left ${
                    formData.projectType === ProjectType.BROWN_FIELD
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleFieldChange('projectType', ProjectType.BROWN_FIELD)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      formData.projectType === ProjectType.BROWN_FIELD ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <WrenchScrewdriverIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Brown Field</h3>
                      <p className="text-sm text-gray-500">Enhancement of existing systems</p>
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="projectType"
                    value="brown_field"
                    checked={formData.projectType === ProjectType.BROWN_FIELD}
                    onChange={() => { /* Handled by button click */ }}
                    className="absolute top-4 right-4"
                  />
                </button>
              </div>
              {errors.projectType && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  {errors.projectType}
                </p>
              )}

              {/* Project Type Change Warning */}
              {isProjectTypeChangeLocked && (
                <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                    <h4 className="text-sm font-medium text-yellow-800">
                      Project Type Change Not Allowed
                    </h4>
                  </div>
                  <p className="mt-1 text-sm text-yellow-700">
                    Changing the project type may affect existing context and analysis. 
                    Make sure this change aligns with your project requirements.
                  </p>
                </div>
              )}
            </fieldset>

          </div>

          {/* Actions — hidden in create mode (handled by BottomNavigationBar) */}
          {!hideActions && (
            <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 main-button"
                disabled={isLoading}
              >
                Cancel
              </button>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#336094] text-white rounded-lg hover:bg-[#6e94c0] focus:outline-none focus:ring-2 focus:ring-[#0097a9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed main-button"
                  disabled={isLoading || isProjectTypeChangeLocked}
                >
                  {isLoading
                    ? 'Saving...'
                    : mode === 'create'
                      ? 'Create Project'
                      : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
});

ProjectForm.displayName = 'ProjectForm';
